import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, ArrowUpRight, Zap, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { LogoWhite } from '../Logo';
import { PWAFooterButton } from '../PWAInstall';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-[#0A1628] text-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#014DB7]/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#84CC16]/5 rounded-full blur-3xl" />
      </div>

      {/* Newsletter Section */}
      <div className="relative border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <h3 className="text-2xl lg:text-3xl font-bold mb-2">Stay Updated</h3>
              <p className="text-white/60">Get the latest DME industry insights and product updates.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="px-5 py-3.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-[#84CC16] focus:outline-none focus:ring-2 focus:ring-[#84CC16]/20 min-w-[280px]"
              />
              <Button className="btn-lift bg-[#84CC16] text-[#0A1628] hover:bg-[#A3E635] rounded-xl px-6 py-3.5 font-bold shadow-glow-lime">
                Subscribe
                <Zap className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-12">
          {/* Brand Section - Left */}
          <div className="lg:w-1/4">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_afcfd48d-f984-4aea-90e8-c216ef326a54/artifacts/vkm50lzv_dme-rus-logo-2.png" 
                alt="DME R'US Logo" 
                className="h-[205px]"
              />
            </Link>
            <p className="text-white/60 leading-relaxed mb-8 max-w-sm">
              Enterprise-grade durable medical equipment management platform. 
              Built for compliance. Designed for growth. Ready for scale.
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin"
                className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center hover:bg-[#014DB7] transition-all duration-300 group"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5 group-hover:scale-110 transition-transform" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-twitter"
                className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center hover:bg-[#014DB7] transition-all duration-300 group"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5 group-hover:scale-110 transition-transform" />
              </a>
            </div>
          </div>

          {/* Center Section - Product & Company */}
          <div className="flex flex-col sm:flex-row gap-12 lg:gap-24">
            {/* Product Links */}
            <div>
              <h4 className="font-bold text-sm uppercase tracking-wider mb-6 text-[#84CC16]">
                Product
              </h4>
              <ul className="space-y-4">
                <li>
                  <Link to="/features" className="text-white/60 hover:text-white text-sm transition-colors inline-flex items-center gap-1 group font-medium">
                    Features
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
                <li>
                  <Link to="/demo" className="text-white/60 hover:text-white text-sm transition-colors inline-flex items-center gap-1 group font-medium">
                    Request Demo
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-white/60 hover:text-white text-sm transition-colors inline-flex items-center gap-1 group font-medium">
                    Pricing
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h4 className="font-bold text-sm uppercase tracking-wider mb-6 text-[#84CC16]">
                Company
              </h4>
              <ul className="space-y-4">
                <li>
                  <Link to="/about" className="text-white/60 hover:text-white text-sm transition-colors inline-flex items-center gap-1 group font-medium">
                    About Us
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-white/60 hover:text-white text-sm transition-colors inline-flex items-center gap-1 group font-medium">
                    Contact
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
                <li>
                  <Link to="/hipaa" className="text-white/60 hover:text-white text-sm transition-colors inline-flex items-center gap-1 group font-medium">
                    HIPAA
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Info - Right */}
          <div className="lg:w-1/4">
            <h4 className="font-bold text-sm uppercase tracking-wider mb-6 text-[#84CC16]">
              Contact
            </h4>
            <ul className="space-y-4">
              <li>
                <a href="mailto:info@dmerus.com" className="flex items-center gap-4 text-white/60 text-sm hover:text-white transition-colors group">
                  <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center group-hover:bg-[#014DB7]/50 transition-colors">
                    <Mail className="w-4 h-4 text-[#84CC16]" />
                  </div>
                  <span className="font-medium">info@dmerus.com</span>
                </a>
              </li>
              <li>
                <a href="tel:+19414664363" className="flex items-center gap-4 text-white/60 text-sm hover:text-white transition-colors group">
                  <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center group-hover:bg-[#014DB7]/50 transition-colors">
                    <Phone className="w-4 h-4 text-[#84CC16]" />
                  </div>
                  <span className="font-medium">(941) 466-4-DME</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-4 text-white/60 text-sm">
                  <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-[#84CC16]" />
                  </div>
                  <span className="leading-relaxed font-medium">11161 E State Rd 70<br />Ste 110-556<br />Lakewood Ranch, FL 34202</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/40 text-sm font-medium">
              © {currentYear} DME R'US. All rights reserved.
            </p>
            <div className="flex items-center gap-8">
              <Link to="/privacy" className="text-white/40 hover:text-white text-sm transition-colors font-medium">
                Privacy
              </Link>
              <Link to="/terms" className="text-white/40 hover:text-white text-sm transition-colors font-medium">
                Terms
              </Link>
              <Link to="/hipaa" className="text-white/40 hover:text-white text-sm transition-colors font-medium">
                HIPAA
              </Link>
              <Link to="/crm/login" className="text-white/40 hover:text-[#84CC16] text-sm transition-colors font-medium">
                Admin Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
